import { useEffect, useState, type ReactNode } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: ReactNode;
  confirmText?: string;
  cancelText?: string;
  confirmVariant?: "default" | "destructive";
  isPending?: boolean;
  errorMessage?: string;
  /** Wenn gesetzt, muss User diesen Text exakt eintippen damit Confirm aktivierbar wird. */
  requiredTypeText?: string;
  /** Extra-Controls (z.B. Optionen-Checkbox) zwischen Beschreibung und Type-Gate. */
  children?: ReactNode;
  onConfirm: () => void;
}

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmText = "Bestätigen",
  cancelText = "Abbrechen",
  confirmVariant = "default",
  isPending = false,
  errorMessage,
  requiredTypeText,
  children,
  onConfirm,
}: ConfirmDialogProps) {
  const [typedText, setTypedText] = useState("");

  useEffect(() => {
    if (!open) setTypedText("");
  }, [open]);

  const typeGateOk = !requiredTypeText || typedText === requiredTypeText;
  const canConfirm = !isPending && typeGateOk;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="confirm-dialog">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        {children}
        {requiredTypeText && (
          <div className="space-y-1">
            <label className="text-xs text-subtext0">
              Tippe <code className="text-text font-mono">{requiredTypeText}</code> zur Bestätigung:
            </label>
            <Input
              value={typedText}
              onChange={(e) => setTypedText(e.target.value)}
              data-testid="confirm-type-input"
              autoFocus
            />
          </div>
        )}
        {errorMessage && (
          <div className="text-sm text-red" data-testid="confirm-error">
            {errorMessage}
          </div>
        )}
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isPending}
            data-testid="confirm-cancel"
          >
            {cancelText}
          </Button>
          <Button
            variant={confirmVariant}
            onClick={onConfirm}
            disabled={!canConfirm}
            data-testid="confirm-submit"
          >
            {isPending ? "..." : confirmText}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
